import React from 'react'
import { logError, LogErrorMetrics } from '../../lib/tracking/felog'
import { feMetrics } from '@lib/tracking/metrics'
import * as CmsMetrics from '@lib/tracking/metrics/metrics'
import { transformToObject } from '../../lib/utils/error'
import { ErrorFallback } from './error-fallback'

const wontFixReactCrashMessage = "Won't Fix React crash" // 'wontFix' are known errors we intentionally are not fixing. There are no alerts associated with them.
const expectedReactCrashMessage = 'Expected React crash' // 'expected' are known errors we intend to fix that are prioritized. There are alerts associated with them at a higher threshold.
const unexpectedReactCrashMessage = 'Unexpected React crash' // 'unexpected' are errors we should look into and categorize as 'expected' or 'wontFix'. There are alerts associated with them at a low threshold.

const wontFixErrorMessages: Set<string> = new Set([])

const exactKnownErrorMessages: Set<string> = new Set([
  // https://grammarly.atlassian.net/browse/GSF-3834
  'Your focus-trap must have at least one container with at least one tabbable node in it at all times',
  // https://grammarly.atlassian.net/browse/GSF-3835
  "Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.",
  "Failed to execute 'insertBefore' on 'Node': The node before which the new node is to be inserted is not a child of this node.",
  'PerformanceEventTiming is not defined',
  'Node.removeChild: The node to be removed is not a child of this node',
])
const prefixKnownErrorMessages: Array<string> = []

interface ErrorBoundaryProps {
  children: React.ReactNode
  fallback?: React.ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)

    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    try {
      const message = wontFixErrorMessages.has(error.message)
        ? wontFixReactCrashMessage
        : exactKnownErrorMessages.has(error.message) ||
            prefixKnownErrorMessages.some((knownError) => error.message.startsWith(knownError))
          ? expectedReactCrashMessage
          : unexpectedReactCrashMessage

      logError(`Boundary Error: ${message}`, LogErrorMetrics.BoundaryError, {
        componentStack: errorInfo.componentStack,
        error: transformToObject(error),
      })

      feMetrics
        .getMetric(CmsMetrics.GeneralClientMetrics.BoundaryError)
        .getCounter(CmsMetrics.ClientMetricsActions.Failure)
        .setContext({
          alertType: message === expectedReactCrashMessage ? 'expected' : 'unexpected',
        })
        .increment()
    } catch (e) {
      console.error('Error logging boundary error', e)
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return <ErrorFallback resetError={() => this.setState({ hasError: false })} />
    }

    return this.props.children
  }
}

export default ErrorBoundary
